// Planilha de Fechamento (ticket #20): a Fatura em matriz — uma linha por
// Devolução (data + OS do Lote), Tipos movimentados nas colunas, agrupadas por
// Categoria. As Devolvidas de Retidas somam na mesma célula; na tela, a
// linha marca quando inclui peças de lotes anteriores ("+N ant.") — no
// arquivo exportado, que vai para o cliente, não. Exporta em PDF/JPEG/CSV,
// como a Tabela de Preços. Os três formatos só DESENHAM o mesmo modelo
// (`_planilha-modelo.ts`, #92): nenhum refaz conta.

import { modeloPlanilha, type ModeloPlanilha } from "./_planilha-modelo.ts";
import { formatData, hojeISO } from "../../domain/datas.ts";
import { formatReais } from "../../domain/money.ts";
import type { Store } from "../store.ts";
import { el, openModal, botao } from "../ui.ts";
import {
  ALTURA_RODAPE_MARCA,
  alturaCabecalhoFolha,
  baixarCanvasJpeg,
  baixarTexto,
  type CabecalhoFolha,
  carregarLogo,
  cor,
  cortar,
  desenharCabecalhoFolha,
  desenharRodapeMarca,
  fonteDoc,
  imprimirCanvas,
  nomeArquivo,
  quebrarAte,
} from "./_folha-canvas.ts";

export function abrirPlanilha(store: Store, faturaId: string): void {
  const snap = store.get();
  const fatura = snap.faturas.find((f) => f.id === faturaId);
  if (!fatura) return;
  const cliente = snap.clientes.find((c) => c.id === fatura.clienteId)?.nome ?? "—";
  // ADR-0015: a relavagem some sempre dos totais; "incluir" só decide se as
  // linhas dela aparecem
  const modelo = (incluirRelavagem: boolean): ModeloPlanilha => modeloPlanilha(snap, faturaId, { incluirRelavagem });
  const dados = modelo(true);

  openModal(
    "Planilha de Fechamento",
    (fechar) => {
      const periodo = `${formatData(fatura.periodo.de)} a ${formatData(
        fatura.periodo.ate,
      )}`;
      const selo = fatura.numero ? ` · Fatura ${fatura.numero}` : "";
      // na tela mostra o estado; no arquivo exportado, os dados vão pro cabeçalho
      const subtitulo = `${cliente} · ${periodo}${
        fatura.numero ? selo : " · rascunho"
      }`;

      // cabeçalho do arquivo exportado (faixa azul: logo pequena à esquerda,
      // título em branco, e os dados que ficavam acima da tabela lá dentro)
      const cab: CabecalhoFolha = {
        titulo: "FECHAMENTO",
        subtitulo: fatura.numero
          ? `Fatura ${fatura.numero}`
          : "Rascunho — valores ainda podem mudar",
        total: formatReais(dados.totalGeral),
        rotuloTotal: "TOTAL DA FATURA",
        itens: [
          { rotulo: "Cliente", valor: cliente },
          { rotulo: "Período", valor: periodo },
          {
            rotulo: "Vencimento",
            valor: fatura.vencimento
              ? formatData(fatura.vencimento)
              : "Não informado",
          },
          { rotulo: "Emitido em", valor: formatData(hojeISO()) },
        ],
      };
      const rodape = snap.config.rodapeExport;

      const selFormato = el("select", {}) as HTMLSelectElement;
      for (const [v, r] of [
        ["pdf", "PDF"],
        ["jpeg", "Imagem (JPEG)"],
        ["csv", "Planilha (CSV)"],
      ] as const) {
        selFormato.append(el("option", { value: v }, r));
      }
      // ADR-0015: relavagem some sempre dos totais; este checkbox só decide se
      // as linhas dela aparecem no arquivo exportado.
      const incluirRelavagem = el("input", { type: "checkbox" }) as HTMLInputElement;
      incluirRelavagem.checked = true;
      const exportar = botao(
        "primario",
        {},
        "Exportar",
      );
      exportar.addEventListener("click", () => {
        const fmt = selFormato.value;
        const dadosExport = modelo(incluirRelavagem.checked);
        fechar();
        // A data do nome do arquivo é o fim do Período da Fatura, não hoje:
        // é quando o fechamento fecha (pedido do dono).
        if (fmt === "csv") {
          baixarCsv(
            dadosExport,
            cliente,
            fatura.numero,
            nomeArquivo("fechamento", cliente, fatura.periodo.ate, "csv"),
          );
        } else {
          void gerarCanvas(dadosExport, cab, rodape).then(
            (cv) => {
              if (fmt === "jpeg") {
                baixarCanvasJpeg(cv, nomeArquivo("fechamento", cliente, fatura.periodo.ate, "jpg"));
              } else {
                imprimirCanvas(cv, nomeArquivo("fechamento", cliente, fatura.periodo.ate));
              }
            },
          );
        }
      });
      const fecharBtn = botao("secundario", {}, "Fechar");
      fecharBtn.addEventListener("click", fechar);

      return el(
        "div",
        { class: "planilha" },
        el("p", { class: "muted planilha-sub" }, subtitulo),
        dados.linhas.length === 0
          ? el("p", { class: "muted mini-vazio" }, "Nenhuma devolução no período.")
          : el(
              "div",
              { class: "table-wrap" },
              tabelaHtml(dados),
            ),
        // grupo de exportação: uma linha abaixo da tabela, largura toda
        el(
          "div",
          { class: "planilha-export" },
          el("label", { class: "filtro planilha-formato" }, "Formato", selFormato),
          dados.temRelavagem
            ? el(
                "label",
                { class: "check planilha-check-relavagem" },
                incluirRelavagem,
                " Incluir OS de relavagem",
              )
            : null,
          el("div", { class: "modal-actions" }, fecharBtn, exportar),
        ),
      );
    },
    "amplo",
  );
}

// ------------------------------------------------------------------ HTML ----

function tabelaHtml(m: ModeloPlanilha): HTMLElement {
  // colunas iguais: table-layout: fixed precisa de larguras — 92px por coluna
  // é o mínimo; sobrando espaço no modal, todas crescem igual; faltando, a
  // tabela rola. Data | OS | <tipos> | Total.
  const nTipos = m.colunas.length;
  const colgroup = el("colgroup", {}, ...Array.from({ length: nTipos + 3 }, () => el("col", { style: "width:92px" })));

  const cabCategoria = el(
    "tr",
    { class: "planilha-cab-cat" },
    el("th", { colspan: "2" }, ""), // Data + OS
    ...m.grupos.map((g) => el("th", { class: "r", colspan: String(g.span) }, g.nome)),
    el("th", {}, ""), // Total
  );
  const cabTipos = el(
    "tr",
    { class: "planilha-cab-tipos" },
    el("th", {}, "Data"),
    el("th", { class: "r" }, "OS"),
    ...m.colunas.map((c) => el("th", { class: "r" }, c.nome)),
    el("th", { class: "r" }, "Total"),
  );

  const corpo = m.linhas.map((l) =>
    el(
      "tr",
      l.cinza ? { class: "planilha-linha-cinza" } : {},
      el("td", {}, l.data, l.deRetidas > 0 ? el("small", { class: "muted planilha-marca" }, `+${l.deRetidas} ant.`) : null),
      el("td", { class: "r num" }, l.os),
      ...l.quantidades.map((q) => el("td", { class: "r num" }, String(q))),
      el("td", { class: "r num" }, el("b", {}, String(l.total))),
    ),
  );

  const linhaRodape = (rotulo: string, celulas: (string | Node)[], total: string, cls = ""): HTMLElement =>
    el(
      "tr",
      cls ? { class: cls } : {},
      el("td", { colspan: "2" }, rotulo),
      ...celulas.map((c) => el("td", { class: "r num" }, c)),
      el("td", { class: "r num" }, total),
    );
  const precos = (ps: number[]): string | Node =>
    ps.length === 1 ? formatReais(ps[0]!) : el("span", { class: "planilha-precos" }, ...ps.map((p) => el("span", {}, formatReais(p))));

  return el(
    "table",
    { class: "mini planilha-tab" },
    colgroup,
    el("thead", {}, cabCategoria, cabTipos),
    el("tbody", {}, ...corpo),
    el(
      "tfoot",
      {},
      linhaRodape("Total de peças", m.totalPecas.porTipo.map(String), String(m.totalPecas.total), "planilha-total"),
      linhaRodape("% do total", m.percentuais.porTipo, m.percentuais.total),
      linhaRodape("Valor unitário", m.unitarios.map(precos), ""),
      linhaRodape("Subtotal", m.subtotal.porTipo.map(formatReais), formatReais(m.subtotal.total), "planilha-total"),
      ...m.ajustes.map((a) =>
        el("tr", { class: "planilha-ajuste" }, el("td", { colspan: String(nTipos + 2) }, a.descricao), el("td", { class: "r num" }, formatReais(a.centavos))),
      ),
      el(
        "tr",
        { class: "planilha-geral" },
        el("td", { colspan: String(nTipos + 2) }, el("b", {}, "Total geral")),
        el("td", { class: "r num" }, el("b", {}, formatReais(m.totalGeral))),
      ),
    ),
  );
}

// ------------------------------------------------------------------- CSV ----

function baixarCsv(m: ModeloPlanilha, cliente: string, numero: string | null, nomeArquivo: string): void {
  const linhaCategoria: string[] = ["", ""]; // Data + OS
  for (const g of m.grupos) {
    linhaCategoria.push(g.nome);
    for (let i = 1; i < g.span; i++) linhaCategoria.push("");
  }
  linhaCategoria.push(""); // Total
  // R$ como número decimal (vírgula), sem o símbolo: o CSV é para conta em planilha
  const reais = (c: number): string => (c / 100).toFixed(2).replace(".", ",");
  const linhas: string[] = [
    numero ? `Fechamento;${cliente};Fatura ${numero}` : `Fechamento;${cliente}`,
    linhaCategoria.join(";"),
    ["Data", "OS", ...m.colunas.map((c) => c.nome), "Total"].join(";"),
    ...m.linhas.map((l) => [l.data, l.os, ...l.quantidades.map(String), String(l.total)].join(";")),
    ["Total de peças", "", ...m.totalPecas.porTipo.map(String), String(m.totalPecas.total)].join(";"),
    ["% do total", "", ...m.percentuais.porTipo, m.percentuais.total].join(";"),
    ["Valor unitário", "", ...m.unitarios.map((ps) => ps.map(reais).join(" / ")), ""].join(";"),
    ["Subtotal", "", ...m.subtotal.porTipo.map(reais), reais(m.subtotal.total)].join(";"),
    ...m.ajustes.map((a) => `${a.descricao};;${reais(a.centavos)}`),
    `Total geral;;${reais(m.totalGeral)}`,
  ];
  baixarTexto(nomeArquivo, linhas.join("\r\n"));
}

// ---------------------------------------------------------------- canvas ----

async function gerarCanvas(
  m: ModeloPlanilha,
  cab: CabecalhoFolha,
  rodape: { slogan: string; whatsapp: string },
): Promise<HTMLCanvasElement> {
  const logo = await carregarLogo();

  const PAD = 32;
  const S = 3;
  const nTipos = m.colunas.length;
  const nCol = nTipos + 3; // Data, OS, <tipos>, Total
  // todas as colunas com a mesma largura
  const col = Math.max(68, Math.min(104, 760 / nCol));
  const W = Math.max(520, PAD * 2 + col * nCol);
  const rowH = 24;
  const headH = 34;
  const catH = 20;

  const nRodape = 4 + m.ajustes.length; // Total de peças, %, unitário, subtotal, + ajustes
  // "Valor unitário" com mais de um preço empilha um por linha (não cabe lado a lado)
  const alturaPreco = 12;
  const maxPrecos = Math.max(1, ...m.unitarios.map((ps) => ps.length));
  const extraUnitario = (maxPrecos - 1) * alturaPreco;
  const hRodape = ALTURA_RODAPE_MARCA;
  const yMatriz = alturaCabecalhoFolha(true) + 14;
  const H = yMatriz + catH + headH + (m.linhas.length + nRodape) * rowH + extraUnitario + (rowH + 4) + 22 + hRodape;

  const cv = document.createElement("canvas");
  cv.width = W * S;
  cv.height = H * S;
  const ctx = cv.getContext("2d");
  if (!ctx) return cv;
  ctx.scale(S, S);
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = cor("surface");
  ctx.fillRect(0, 0, W, H);

  desenharCabecalhoFolha(ctx, W, logo, cab);
  let y = yMatriz;

  // x de referência de cada coluna (0 = Data, 1 = OS, 2.. = tipos, última = Total)
  const xR = (c: number): number => PAD + col * (c + 1) - 6;
  const xL = (c: number): number => PAD + col * c + 4;
  const cTipo = (i: number): number => 2 + i;
  const cTotal = 2 + nTipos;

  // faixa de Categoria acima do cabeçalho dos Tipos
  ctx.font = fonteDoc("corpo", "700");
  ctx.fillStyle = cor("muted");
  ctx.textAlign = "center";
  {
    let c = 2;
    for (const g of m.grupos) {
      const x0 = PAD + col * c;
      const x1 = x0 + col * g.span;
      ctx.strokeStyle = cor("border");
      ctx.beginPath();
      ctx.moveTo(x0 + 3, y + catH - 3);
      ctx.lineTo(x1 - 3, y + catH - 3);
      ctx.stroke();
      ctx.fillText(cortar(ctx, g.nome.toUpperCase(), col * g.span - 8), (x0 + x1) / 2, y + 12);
      c += g.span;
    }
  }
  ctx.textAlign = "left";
  y += catH;

  // cabeçalho: Data | OS | <tipos, com quebra em 2 linhas> | Total
  ctx.fillStyle = cor("surface-alt");
  ctx.fillRect(PAD, y, W - PAD * 2, headH);
  ctx.fillStyle = cor("muted");
  ctx.font = fonteDoc("corpo", "700");
  ctx.textAlign = "left";
  ctx.fillText("DATA", xL(0), y + headH / 2 + 3);
  ctx.textAlign = "right";
  ctx.fillText("OS", xR(1), y + headH / 2 + 3);
  m.colunas.forEach((c, i) => {
    const linhasNome = quebrarAte(ctx, c.nome.toUpperCase(), col - 8, 2);
    if (linhasNome.length === 1) {
      ctx.fillText(linhasNome[0]!, xR(cTipo(i)), y + headH / 2 + 3);
    } else {
      ctx.fillText(linhasNome[0]!, xR(cTipo(i)), y + 14);
      ctx.fillText(linhasNome[1]!, xR(cTipo(i)), y + 26);
    }
  });
  ctx.fillText("TOTAL", xR(cTotal), y + headH / 2 + 3);
  y += headH;

  // corpo (fonte -2px: 10 no lugar de 12)
  ctx.font = fonteDoc("corpo");
  for (const l of m.linhas) {
    if (l.cinza) {
      ctx.fillStyle = cor("surface-alt");
      ctx.fillRect(PAD, y, W - PAD * 2, rowH);
    }
    ctx.strokeStyle = cor("surface-alt");
    ctx.beginPath();
    ctx.moveTo(PAD, y + rowH);
    ctx.lineTo(W - PAD, y + rowH);
    ctx.stroke();

    ctx.fillStyle = l.cinza ? cor("muted") : cor("ink");
    ctx.textAlign = "left";
    // Só a data: as Devolvidas de Retidas já somam na célula da peça, e o
    // arquivo vai para o cliente sem a marca "+N ant." (decisão do dono).
    ctx.fillText(l.data, xL(0), y + 15);
    ctx.textAlign = "right";
    ctx.fillText(l.cinza ? cortar(ctx, l.os, col - 8) : l.os, xR(1), y + 15);
    l.quantidades.forEach((q, i) => ctx.fillText(String(q), xR(cTipo(i)), y + 15));
    ctx.font = fonteDoc("corpo", "700");
    ctx.fillText(String(l.total), xR(cTotal), y + 15);
    ctx.font = fonteDoc("corpo");
    y += rowH;
  }

  // rodapé
  const linhaRod = (rotulo: string, celulas: (string | string[])[], total: string, forte = false, extra = 0): void => {
    if (forte) {
      ctx.fillStyle = cor("surface-alt");
      ctx.fillRect(PAD, y, W - PAD * 2, rowH + extra);
    }
    ctx.fillStyle = cor("ink");
    ctx.font = fonteDoc("corpo", forte ? "700" : "");
    ctx.textAlign = "left";
    ctx.fillText(rotulo, xL(0), y + 15);
    ctx.textAlign = "right";
    celulas.forEach((v, i) => {
      (Array.isArray(v) ? v : [v]).forEach((t, k) => ctx.fillText(t, xR(cTipo(i)), y + 15 + k * alturaPreco));
    });
    if (total) ctx.fillText(total, xR(cTotal), y + 15);
    y += rowH + extra;
  };
  linhaRod("Total de peças", m.totalPecas.porTipo.map(String), String(m.totalPecas.total), true);
  linhaRod("% do total", m.percentuais.porTipo, m.percentuais.total);
  linhaRod("Valor unitário", m.unitarios.map((ps) => ps.map(formatReais)), "", false, extraUnitario);
  linhaRod("Subtotal", m.subtotal.porTipo.map(formatReais), formatReais(m.subtotal.total), true);
  for (const a of m.ajustes) linhaRod(a.descricao, [], formatReais(a.centavos));

  // total geral — fonte cheia (não recebe o -2px)
  ctx.fillStyle = cor("brand-navy-deep");
  ctx.fillRect(PAD, y, W - PAD * 2, rowH + 4);
  ctx.fillStyle = cor("surface");
  ctx.font = fonteDoc("lista", "700");
  ctx.textAlign = "left";
  ctx.fillText("TOTAL GERAL", PAD + 6, y + 18);
  ctx.textAlign = "right";
  ctx.fillText(formatReais(m.totalGeral), W - PAD - 6, y + 18);
  y += rowH + 4;

  desenharRodapeMarca(ctx, W, H - hRodape, rodape);
  return cv;
}